import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import type { PaginatedResponse, ReportCollectionItem } from "@/types/api";

const PAGE_SIZE = 60;

/**
 * A collection's contents, newest first.
 *
 * Tombstones come back in the list rather than being filtered server-side: an
 * item deleted from the project has to stay visible, or a report that quietly
 * comes back shorter is nobody's fault and nobody's problem until a client
 * notices.
 */
export function useCollectionItems(projectId: string, collectionId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useInfiniteQuery({
    queryKey: collectionKeys.items(projectId, collectionId),
    queryFn: ({ pageParam }) =>
      fetchWithAuth<PaginatedResponse<ReportCollectionItem>>(
        `/reports/3.0/projects/${projectId}/collections/${collectionId}/items/` +
          `?page=${pageParam}&page_size=${PAGE_SIZE}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    select: (data) => ({
      items: data.pages.flatMap((page) => page.results),
      totalCount: data.pages[0]?.count ?? 0,
    }),
    staleTime: 30_000,
  });
}
